import React, { Component } from 'react';
import Tree from 'react-ui-tree';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import {
  AddIcon,
  DeleteIcon,
  FolderIcon,
  InfoCircleIcon,
  SaveIcon,
  UndoIcon,
  UserIcon,
  LockIcon,
} from '@/shared/components/Icons';
import intlShape from '@/shared/utils/intlShape';
import handleHttpError from '@/shared/services/HandleHttpError';
import Button from '@/shared/components/Button';
import YesNoModal from '@/shared/components/YesNoModal';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import SimplePanel from '@/shared/components/SimplePanel';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import DataCacheService from '@/shared/services/DataCacheService';
import Checkbox from '@/shared/components/input/Checkbox';
import { FaUser, FaUserShield } from 'react-icons/fa';
import api from '../../../shared/api';
import config from '../../../config';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import FixedTree from '../../../shared/components/FixedTree';
import {
  CanCreate,
  CanDelete,
  CanRead,
  CanUpdate,
  showWhenPermission,
} from '../../../shared/components/security';
import messages from './teamMessages';
import SelectEmployeeModal from './SelectEmployeeModal';
import ToastrService from '../../../shared/services/ToastrService';

const employeeSortFunc = dynamicSortMultiple('inheritedTeam', 'firstName', 'lastName', 'email');

class TeamsContainer extends Component {
  state = { tree: null, tags: null, newTag: '', saving: false, restructure: false };

  mapTreeNode = ({ children = null, ...node }) => {
    const result = {
      ...node,
      collapsed: false,
      children: children && children.length ? children.map(this.mapTreeNode) : [],
    };
    return result;
  };

  componentDidMount() {
    const { intl } = this.props;

    const users = (DataCacheService.getDataCache()?.user || [])
      .filter(u => u.isActive) // Only active users
      .map(u => ({
        id: u.userID,
        name: `${u.firstName} ${u.lastName}`,
        employeeId: u.employeeID,
      }))
      .sort(dynamicSort('name'));

    api.base
      .get(`${config.appUrls.api}/api/team/get`)
      .then(({ data }) => {
        this.setState({
          tree: { children: data.map(this.mapTreeNode), collapsed: false },
          users,
        });
      })
      .catch(handleHttpError(intl));
  }

  createTeam = team => {
    const { saving } = this.state;
    if (saving) return;

    this.setState({ saving: true });
    return api.base.post(`${config.appUrls.api}/api/team/create`, team).then(
      ({ data }) => {
        this.setState({
          tree: { children: data.map(this.mapTreeNode), collapsed: false },
          saving: false,
        });
      },
      error => {
        const { intl } = this.props;

        this.setState({ saving: false });
        handleHttpError(intl)(error);
        return null;
      },
    );
  };

  AddTeamToOtherTeam = (team, name) => {
    name = name.replace(';', '');
    if (!name) return;

    const newTeam = {
      parentId: team ? team.id : null,
      name,
    };

    this.createTeam(newTeam);
  };

  deleteTeam = activeDeleteTeam => {
    const { activeTeam, saving } = this.state;
    if (saving) return;
    this.setState({ saving: true });

    return api.base
      .delete(`${config.appUrls.api}/api/team/deleteTeam?id=${activeDeleteTeam.id}`)
      .then(
        ({ data }) => {
          this.setState(state => ({
            showDeleteTeamModal: false,
            tree: { children: data.map(this.mapTreeNode), collapsed: false },
            // If the team's employees are shown on the right side of the screen and this delete is deleted, set the employees array to null
            employees:
              activeTeam && activeTeam.id === activeDeleteTeam.id ? undefined : state.employees,
            activeTeam: undefined,
            saving: false,
          }));
        },
        error => {
          const { intl } = this.props;
          this.setState({ saving: false });
          handleHttpError(intl)(error);
          return null;
        },
      );
  };

  addTeamToRoot = name => {
    if (!name) return;
    this.createTeam({
      name,
    });
  };
  deleteCollapsedStateRecursive = ({ children, ...currentNode }) => {
    const copy = {
      ...currentNode,
      children: children.map(c => this.deleteCollapsedStateRecursive(c)),
    };
    delete copy.collapsed;

    return copy;
  };

  onTeamSelected = team => {
    this.setState({ activeTeam: team });

    this.retrieveEmployeesInTeam(team);
  };

  retrieveEmployeesInTeam = team => {
    api.base.get(`${config.appUrls.api}/api/team/employees?id=${team.id}`).then(({ data }) => {
      this.setState({
        employees: data.sort(employeeSortFunc),
      });
    });
  };

  addEmployeeToTeam = (employee, team) => {
    const { intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/team/AddEmployeeToTeam`, {
        employeeId: employee.employeeId,
        teamId: team.id,
      })
      .then(({ data }) => {
        this.setState({
          showSelectEmployeeModal: false,
          employees: data.sort(employeeSortFunc),
        });
        ToastrService.success(
          intl.formatMessage(messages.EmployeeXAddedToTeamX, {
            employee: employee.name,
            team: team.name,
          }),
        );
      })
      .catch(error => handleHttpError(intl)(error));
  };

  deleteEmployeeFromTeam = (employee, team) => {
    const { intl } = this.props;

    return api.base
      .post(`${config.appUrls.api}/api/team/DeleteEmployeeFromTeam`, {
        employeeId: employee.employeeId,
        teamId: team.id,
      })
      .then(({ data }) => {
        this.setState({
          showDeleteEmployeeModal: false,
          employees: data.sort(employeeSortFunc),
        });
        ToastrService.success(
          intl.formatMessage(messages.EmployeeXRemovedFromTeamX, {
            employee: `${employee.firstName} ${employee.lastName}`,
            team: team.name,
          }),
        );
      })
      .catch(error => handleHttpError(intl)(error));
  };
  startRestructure = () => {
    const { tree } = this.state;
    // Store copy of original tree state
    const treeCopy = this.deleteCollapsedStateRecursive(tree);

    this.setState({ restructure: true, treeCopy });
  };

  saveRestructure = () => {
    const { treeCopy, tree, activeTeam, saving } = this.state;
    if (saving) return;
    const ignoreCollapsedTree = this.deleteCollapsedStateRecursive(tree);

    if (JSON.stringify(treeCopy) === JSON.stringify(ignoreCollapsedTree)) {
      this.setState({ restructure: false, treeCopy: null });

      return;
    }
    this.setState({ saving: true });

    return api.base.put(`${config.appUrls.api}/api/Team`, tree.children).then(
      ({ data }) => {
        const newTree = { children: data.map(this.mapTreeNode), collapsed: false };

        this.setState(
          {
            tree: newTree,
            treeCopy: null,
            restructure: false,
            saving: false,
          },
          () => {
            if (!activeTeam) return;
            this.retrieveEmployeesInTeam(activeTeam);
          },
        );
        const { intl } = this.props;
        ToastrService.changesSaved(intl);
        return true;
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        this.setState({ saving: false });
      },
    );
  };

  cancelRestructure = () => {
    const { treeCopy, saving } = this.state;
    if (saving) return;

    this.setState({ restructure: false, tree: treeCopy, treeCopy: null });
  };

  toggleCanViewInvoices = (employee, team, canViewInvoices) => {
    if (employee.inheritedTeam && employee.canViewInvoices) return;
    const { saving } = this.state;
    if (saving) return;
    this.setState({
      saving: true,
    });
    const model = { employeeId: employee.employeeId, canViewInvoices, teamId: team.id };
    return api.base.put(`${config.appUrls.api}/api/Team/CanViewInvoices`, model).then(
      ({ data }) => {
        this.setState({
          employees: data.sort(employeeSortFunc),
          saving: false,
        });
        ToastrService.success();
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        this.setState({
          saving: false,
        });
      },
    );
  };

  renderTeam = team => {
    const { intl } = this.props;
    const { saving, restructure } = this.state;

    const deleteTitle = `${intl.formatMessage(messages.DeleteTeam)} '${team.name}'`;

    return (
      <div className="node">
        {!restructure ? (
          <a
            className="node-name"
            onClick={() => {
              document.activeElement.blur();
              this.onTeamSelected(team);
            }}
          >
            {team.name}
          </a>
        ) : (
          <span className="node-name">{team.name}</span>
        )}
        {!restructure && (
          <span className="pull-right hide-on-drag">
            <CanRead on="teams">
              <button
                type="button"
                title={intl.formatMessage(messages.ViewEmployeesInTeamX, { team: team.name })}
                className="btn btn-xs margin5R btn-default"
                onMouseDown={e => e.stopPropagation()}
                onClick={() => {
                  document.activeElement.blur();
                  this.onTeamSelected(team);
                }}
              >
                <UserIcon
                  tooltip={intl.formatMessage(messages.ViewEmployeesInTeamX, { team: team.name })}
                />
              </button>
            </CanRead>
            <CanCreate on="teams">
              <span className="margin5R" onMouseDown={e => e.stopPropagation()}>
                <EditableTextbox
                  value=""
                  placeholder={intl.formatMessage(messages.AddTeamToOtherTeamX, {
                    team: team.name,
                  })}
                  list="tags"
                  onChange={tag => this.AddTeamToOtherTeam(team, tag)}
                  disabled={saving}
                  displayValue={
                    <button
                      type="button"
                      className="btn btn-xs btn-default"
                      title={intl.formatMessage(messages.AddTeamToOtherTeamX, { team: team.name })}
                    >
                      <AddIcon
                        tooltip={intl.formatMessage(messages.AddTeamToOtherTeamX, {
                          team: team.name,
                        })}
                      />
                    </button>
                  }
                  inputClassName="tag-input input-sm"
                />
              </span>
            </CanCreate>
            <CanDelete on="teams">
              <button
                type="button"
                title={deleteTitle}
                disabled={saving}
                className="btn btn-xs btn-default"
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  this.setState({ activeDeleteTeam: team, showDeleteTeamModal: true });
                  e.stopPropagation();
                }}
              >
                <DeleteIcon tooltip={deleteTitle} />
              </button>
            </CanDelete>
          </span>
        )}
      </div>
    );
  };

  render() {
    const {
      tree,
      tags,
      newTag,
      activeTeam,
      employees,
      showSelectEmployeeModal,
      showDeleteTeamModal,
      showDeleteEmployeeModal,
      activeDeleteTeam,
      users,
      activeEmployee,
      saving,
      restructure,
    } = this.state;
    const { intl } = this.props;

    const filteredUsers =
      users && employees
        ? users.filter(u => employees.findIndex(e => e.employeeID === u.employeeId) < 0)
        : [];
    return (
      <SimplePanel article="5434798053266" title={intl.formatMessage(messages.Teams)}>
        <div className="info-block container-fluid">
          <div className="row">
            {showSelectEmployeeModal && activeTeam && (
              <SelectEmployeeModal
                team={activeTeam}
                users={filteredUsers}
                onClose={() =>
                  this.setState(state => ({
                    ...state,
                    showSelectEmployeeModal: false,
                  }))
                }
                onSave={this.addEmployeeToTeam}
              />
            )}

            {showDeleteEmployeeModal && activeTeam && activeEmployee && (
              <YesNoModal
                intl={intl}
                show
                title={intl.formatMessage(messages.RemoveEmployeeFromTeam)}
                message={intl.formatMessage(messages.RemoveEmployeeFromTeamMsg, {
                  employee: `${activeEmployee.firstName} ${activeEmployee.lastName}`,
                  team: activeTeam.name,
                })}
                onNo={() =>
                  this.setState(state => ({
                    ...state,
                    showDeleteEmployeeModal: false,
                    activeEmployee: undefined,
                  }))
                }
                onSave={() => this.deleteEmployeeFromTeam(activeEmployee, activeTeam)}
              />
            )}

            {showDeleteTeamModal && activeDeleteTeam && (
              <YesNoModal
                intl={intl}
                show
                title={intl.formatMessage(messages.RemoveTeam)}
                message={intl.formatMessage(messages.RemoveTeamXMsg, {
                  team: activeDeleteTeam.name,
                })}
                onNo={() =>
                  this.setState(state => ({
                    ...state,
                    showDeleteTeamModal: false,
                    activeDeleteTeam: undefined,
                  }))
                }
                onSave={() => this.deleteTeam(activeDeleteTeam)}
              />
            )}

            <div className="col-md-4">
              {tree ? (
                <div>
                  <div className="document-tree hidden-root">
                    <CanCreate on="teams">
                      <span>
                        <div className="input-group" style={{ maxWidth: '300px' }}>
                          <input
                            type="text"
                            maxLength="200"
                            className="form-control tag-input"
                            value={newTag}
                            placeholder={intl.formatMessage(messages.CreateNewTeam)}
                            onChange={e =>
                              this.setState({ newTag: e.target.value.replace(';', '') })
                            }
                            list="tags"
                            disabled={saving || restructure}
                            autoComplete="off"
                            onKeyDown={e => {
                              if (e.keyCode === 13) {
                                this.addTeamToRoot(newTag);
                                this.setState({ newTag: '' });
                              } else if (e.keyCode === 27) {
                                this.setState({ newTag: '' });
                              }
                            }}
                          />
                          <span className="input-group-btn">
                            <button
                              type="button"
                              className="btn btn-default"
                              disabled={!newTag || saving || restructure}
                              onClick={() => {
                                if (!newTag || saving) return;
                                this.addTeamToRoot(newTag);
                                this.setState({ newTag: '' });
                              }}
                            >
                              <AddIcon tooltip={intl.formatMessage(messages.CreateNewTeam)} />
                            </button>
                          </span>
                        </div>
                      </span>
                    </CanCreate>

                    <CanUpdate
                      on="teams"
                      fallback={
                        <FixedTree tree={tree} paddingLeft={20} renderNode={this.renderTeam} />
                      }
                    >
                      {restructure && !saving ? (
                        <Tree paddingLeft={20} tree={tree} renderNode={this.renderTeam} />
                      ) : (
                        <FixedTree tree={tree} paddingLeft={20} renderNode={this.renderTeam} />
                      )}
                    </CanUpdate>
                  </div>
                  <CanUpdate on="teams">
                    <div className="margin10T">
                      {!restructure ? (
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={() => this.startRestructure()}
                        >
                          <FolderIcon />
                          <FormattedMessage {...messages.RestructureTeams} />
                        </button>
                      ) : (
                        <div>
                          <div className="alert alert-info linebreak">
                            <FormattedMessage {...messages.RestructureTeamsInfo} />
                          </div>

                          <Button
                            showProgress
                            btnClassName="btn-inverse margin10R"
                            onClick={() => this.saveRestructure()}
                          >
                            <SaveIcon />
                            &nbsp;
                            <FormattedMessage {...messages.Save} />
                          </Button>
                          <Button onClick={() => this.cancelRestructure()} disabled={saving}>
                            <UndoIcon />
                            &nbsp;
                            <FormattedMessage {...messages.Cancel} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CanUpdate>
                </div>
              ) : (
                <LoadingAnimation />
              )}
            </div>
            <div className="col-md-8">
              {activeTeam && !restructure && (
                <div>
                  <SimplePanel
                    title={intl.formatMessage(messages.EmployeesInTeamX, {
                      team: activeTeam.name,
                    })}
                  />
                  <CanCreate on="teams">
                    <Button
                      btnClassName="btn btn-xs btn-inverse margin10R"
                      onClick={() => this.setState({ showSelectEmployeeModal: true })}
                      disabled={!activeTeam || saving}
                    >
                      <span className="margin5R">
                        <AddIcon className="file-icon" />
                      </span>
                      {intl.formatMessage(messages.AddEmployee)}
                    </Button>
                  </CanCreate>

                  <table className="table table-grid table-hover margin5T">
                    <thead>
                      <tr>
                        <td>{intl.formatMessage(messages.Name)}</td>
                        <td>{intl.formatMessage(messages.Email)}</td>
                        <td className="text-center">
                          {intl.formatMessage(messages.CanViewInvoices)}
                          <ZendeskHelp article="14792873533724" />
                        </td>
                        <td />
                      </tr>
                    </thead>
                    <tbody>
                      {employees &&
                        employees.map(employee => (
                          <tr
                            key={`${employee.employeeId}-${employee.inheritedTeam ? 1 : 0}`}
                            className={classNames({ 'muted-row': employee.inheritedTeam })}
                          >
                            <td
                              className={classNames({
                                'text-muted font-normal': employee.inheritedTeam,
                              })}
                            >
                              {employee.firstName} {employee.lastName}
                            </td>
                            <td
                              className={classNames({
                                'text-muted font-normal': employee.inheritedTeam,
                              })}
                            >
                              {employee.email}
                            </td>
                            <td
                              className={classNames('text-center', {
                                'text-muted font-normal': employee.inheritedTeam,
                              })}
                            >
                              {employee.hasDraftInvoicePermission ? (
                                <Checkbox
                                  onClick={value =>
                                    this.toggleCanViewInvoices(employee, activeTeam, value)
                                  }
                                  value={employee.canViewInvoices}
                                  disabled={
                                    saving || (employee.inheritedTeam && employee.canViewInvoices)
                                  }
                                />
                              ) : null}
                            </td>
                            <td>
                              {/* an employee cannot be removed if it was 'inherited' from one of its parent teams */}
                              {!employee.inheritedTeam ? (
                                !employee.isCompanyLocked ? (
                                  <CanDelete on="teams">
                                    <DeleteIcon
                                      disabled={saving}
                                      onClick={() =>
                                        this.setState({
                                          showDeleteEmployeeModal: true,
                                          activeEmployee: employee,
                                        })
                                      }
                                    />
                                  </CanDelete>
                                ) : (
                                  <>
                                    <OverlayTrigger
                                      trigger={['hover', 'focus']}
                                      placement="bottom"
                                      rootClose
                                      overlay={
                                        <Popover id="popover-trigger-hover-focus">
                                          {intl.formatMessage(messages.TeamsSetByCompany)}
                                        </Popover>
                                      }
                                    >
                                      <span>
                                        <LockIcon />
                                      </span>
                                    </OverlayTrigger>
                                    &nbsp;
                                    {employee.keepAfterCompanyChange ? (
                                      <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement="bottom"
                                        rootClose
                                        overlay={
                                          <Popover id="popover-trigger-hover-focus">
                                            {intl.formatMessage(messages.TeamAlsoAssignedToUser)}
                                          </Popover>
                                        }
                                      >
                                        <span>
                                          <FaUser />
                                        </span>
                                      </OverlayTrigger>
                                    ) : null}
                                  </>
                                )
                              ) : (
                                <OverlayTrigger
                                  trigger={['hover', 'focus']}
                                  placement="bottom"
                                  rootClose
                                  overlay={
                                    <Popover id="popover-trigger-hover-focus">
                                      {intl.formatMessage(
                                        messages.EmployeeInheritedTeamExplanation,
                                        {
                                          team: activeTeam.name,
                                        },
                                      )}
                                    </Popover>
                                  }
                                >
                                  <span>
                                    <InfoCircleIcon />
                                  </span>
                                </OverlayTrigger>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <datalist id="tags">
              {tags && tags.map(t => <option key={t.id} value={t.name} />)}
            </datalist>
          </div>
          {!restructure && saving && <LoadingAnimation />}
        </div>
      </SimplePanel>
    );
  }
}

TeamsContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission('teams', 'read')(injectIntl(TeamsContainer));
